import AdapterDateFns from '@mui/lab/AdapterDateFns'
import LocalizationProvider from '@mui/lab/LocalizationProvider'
import { FormControlLabel, Stack, Typography } from '@mui/material'
import DialogContentText from '@mui/material/DialogContentText'
import { formatISO } from 'date-fns'
import { Field, Form, Formik } from 'formik'
import { Switch, TextField } from 'formik-mui'
import { DateTimePicker } from 'formik-mui-lab'
import { useSnackbar } from 'notistack'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import * as yup from 'yup'

import ProductAutocomplete from '../../../../../components/ProductAutocomplete'
import ResponsiveDialog from '../../../../../components/ResponsiveDialog'
import { createCallBack, editCallBack } from '../../../calendar/api'
import { selectCallBack } from '../../../calendar/slice'
import { loadCommonTypes } from '../../../common-types/api'

function CallBackDialog({ open, callBackId, onSubmit, onClose, ...props }) {
  const dispatch = useDispatch()

  const { enqueueSnackbar } = useSnackbar()

  const callBack = useSelector(selectCallBack(callBackId))

  const [initialValues, setInitialValues] = useState({})

  const handleSubmit = (values) => {
    if (!open) {
      return
    }
    const isCreating = callBackId == null

    const payload = {
      product: values.product,
      call_date: values.call_date,
      is_urgent: values.is_urgent,
      is_complete: values.is_complete,
      call_type: values.call_type,
      notes: values.notes
    }

    if (!isCreating) {
      payload.id = callBackId
    }

    if (values.organization) {
      payload.organization = values.organization
    }

    if (isCreating) {
      dispatch(createCallBack(payload))
        .unwrap()
        .then(() => {
          enqueueSnackbar('Call back created successfully', {
            variant: 'success'
          })
        })
        .catch(({ data }) => {
          const errorString = Object.values(data)[0]
          enqueueSnackbar(`Call back creation failed: ${errorString}`, {
            variant: 'error'
          })
        })
    } else {
      dispatch(editCallBack(payload))
        .unwrap()
        .then(() => {
          enqueueSnackbar('Call back modified successfully', {
            variant: 'success'
          })
        })
        .catch(({ data }) => {
          const errorString = Object.values(data)[0]
          enqueueSnackbar(`Call back modification failed: ${errorString}`, {
            variant: 'error'
          })
        })
        .finally(() => {
          if (onSubmit) {
            onSubmit(payload)
          }
        })
    }

    onClose()
  }

  const handleClose = () => {
    onClose()
  }

  useEffect(() => {
    dispatch(loadCommonTypes()).unwrap()
  }, [])

  useEffect(
    () =>
      setInitialValues({
        product: callBack?.product || '',
        call_date: callBack?.call_date || formatISO(new Date()),
        is_urgent: callBack?.is_urgent || false,
        is_complete: callBack?.is_complete || false,
        call_type: 0,
        notes: callBack?.notes || ''
      }),
    [callBack]
  )

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={yup.object({
        product: yup.number('Select product').required('Product is required'),
        call_date: yup
          .string('Enter call time')
          .required('Call time is required'),
        is_urgent: yup.boolean(),
        is_complete: yup.boolean(),
        call_type: yup.number().required('Call type is required'),
        notes: yup.string()
      })}
      onSubmit={handleSubmit}
    >
      {({ values, submitForm }) => (
        <Form>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <ResponsiveDialog
              title={`${callBackId ? 'Edit' : 'New'} Call Back`}
              open={open}
              onClose={handleClose}
              onSubmit={() => {
                submitForm()
              }}
              {...props}
            >
              <DialogContentText>
                To configure this call back, please enter all required details
                here.
              </DialogContentText>
              <Stack spacing={2}>
                <Typography variant="h4">General</Typography>
                <Stack spacing={2} direction="column">
                  <Field
                    fullWidth
                    name="product"
                    label="Product"
                    value={values.product}
                    inputProps={{
                      fullWidth: true
                    }}
                    component={ProductAutocomplete}
                  />
                  <Field
                    required
                    fullWidth
                    component={DateTimePicker}
                    name="call_date"
                    label="Call Time"
                  />
                  <Field
                    fullWidth
                    multiline
                    minRows={3}
                    name="notes"
                    label="Notes"
                    component={TextField}
                  />
                  <FormControlLabel
                    label="Is Urgent"
                    control={
                      <Field
                        type="checkbox"
                        name="is_urgent"
                        component={Switch}
                      />
                    }
                  />
                  <FormControlLabel
                    label="Is Complete"
                    control={
                      <Field
                        type="checkbox"
                        name="is_complete"
                        component={Switch}
                      />
                    }
                  />
                </Stack>
              </Stack>
            </ResponsiveDialog>
          </LocalizationProvider>
        </Form>
      )}
    </Formik>
  )
}

CallBackDialog.propTypes = {
  open: PropTypes.bool,
  callBackId: PropTypes.number,
  onSubmit: PropTypes.func,
  onClose: PropTypes.func
}

export default CallBackDialog
